/**
 * useClientCapabilities — THE one capability/status model (D2/P2).
 *
 * Every surface that needs to know "is product X ready for the active
 * client?" reads this hook: the picker's ads gate, the ads-ops guard, the
 * settings status strip, the integration cards. One React Query key
 * (`['client-capabilities', tenantId]`), one invalidation path, one 3-state
 * vocabulary (`connected | action_needed | not_set_up`). No derived fact is
 * persisted in a store — capabilities live here and nowhere else.
 *
 * Source strategy (S3): DERIVE from existing payloads behind the single
 * internal `fetchClientCapabilities` source function — the workspace list
 * (has_google_ads / product_package), tenant settings (WP / Google Docs /
 * customer-ID fields), and the Brand Bible record. When the additive
 * backend capabilities endpoint lands (root-gated follow-up), only the body
 * of that one function swaps; no consumer changes.
 *
 * Behavior contract (P2 spine row):
 *  - Optimistic after a user-initiated capability write: the write helpers
 *    below patch the cache immediately, then invalidate so a refetch
 *    confirms. (The stale-badge bug class — a derived flag copied into a
 *    list snapshot nobody refreshed — cannot recur: one key, one invalidation.)
 *  - Background invalidation holds old data while refetching (React Query
 *    default same-key refetch behavior — no flicker, no spinner).
 *  - Fetch failure is NEVER silent last-known: consumers get `isError` and
 *    `dataUpdatedAt` so pills render the muted stale modifier
 *    ("Can't reach — retrying" / "Updated Nm ago"), and downstream actions
 *    gate on verified state, not on the cache's last claim.
 */

import { useQuery, type QueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { brandBibleService } from '../services/brandBibleService';
import {
  getConnectionStatus,
  type GoogleAdsConnectionStatus,
} from '../services/googleAdsOAuthService';
import { getTenantTier } from '../services/googleAdsKpiDashboardService';
import { useWorkspaceStore } from '../stores/useWorkspaceStore';
import {
  resolveProductPackage,
  type ProductPackage,
  type Workspace,
} from '../types/auth';
import type { BrandBible, TenantSettings } from '../types/tenant';

// ---------------------------------------------------------------------------
// Model
// ---------------------------------------------------------------------------

export type CapabilityState = 'connected' | 'action_needed' | 'not_set_up';

export interface ProductCapability {
  state: CapabilityState;
}

export interface ClientCapabilities {
  /** What the agency sold this client — gates destinations, never a fake lock. */
  productPackage: ProductPackage;
  googleAds: ProductCapability & {
    /** Client's 10-digit customer ID is mapped (server-derived or verified). */
    customerMapped: boolean;
    /** Agency OAuth connection active (sourced from the status poll). */
    agencyConnected: boolean;
    /** Google Ads management tier (sourced from the tier read). */
    tier: number | null;
    /** Agency OAuth status — the join moved inside the hook (R5). Drives R2/R3. */
    oauthStatus: GoogleAdsConnectionStatus | null;
  };
  wordpress: ProductCapability & {
    connected: boolean;
    /**
     * Whether the connection claim is backed by a persisted verification
     * (wp_verified_at). Always false until the backend field exists — the
     * WordPress pill renders the "Not verified" modifier, never a bare
     * "Connected" from mere key presence.
     */
    verified: boolean;
  };
  googleDocs: ProductCapability & {
    configured: boolean;
    /**
     * Whether the config is backed by a backend verification. Always false
     * until a verification exists — config-presence renders Connected ONLY
     * with the "Not verified" modifier (parity with WordPress, R7).
     */
    verified: boolean;
  };
  brandBible: ProductCapability & { configured: boolean };
}

export const clientCapabilitiesKeys = {
  all: ['client-capabilities'] as const,
  detail: (tenantId: string) => ['client-capabilities', tenantId] as const,
};

// ---------------------------------------------------------------------------
// Derivation (the single internal source function)
// ---------------------------------------------------------------------------

function normalizeCustomerId(value: string | null | undefined): string {
  return String(value ?? '').replace(/\D/g, '');
}

function deriveGoogleAds(
  workspaceRow: Workspace | undefined,
  settings: TenantSettings | null,
  oauthStatus: GoogleAdsConnectionStatus | null,
  tier: number | null,
): ClientCapabilities['googleAds'] {
  const normalized = normalizeCustomerId(settings?.google_ad_customer_id);
  // A valid 10-digit ID in the hook's OWN fresh settings read confirms the
  // mapping immediately after a write; the workspace-list flag (same
  // server-side normalization, one refresh behind writes) covers the steady
  // state. Either source confirming is enough — OR-precedence makes the
  // derivation immune to the list snapshot's refresh lag in both directions.
  const customerMapped =
    normalized.length === 10 || workspaceRow?.has_google_ads === true;
  const hasRawButInvalid = !customerMapped && normalized.length > 0;
  const agencyConnected = oauthStatus?.connected === true;
  const adsStatus = oauthStatus?.status;
  // R3: the pill stays 3-state; pending/revoked are action-needed (never
  // connected). A transient error while otherwise connected stays Connected
  // (the degraded warning line carries last_error — rendered by the section).
  let state: CapabilityState;
  if (adsStatus === 'pending' || adsStatus === 'revoked') {
    state = 'action_needed';
  } else if (agencyConnected && customerMapped) {
    state = 'connected';
  } else if (agencyConnected || customerMapped || hasRawButInvalid) {
    state = 'action_needed';
  } else {
    state = 'not_set_up';
  }
  return { state, customerMapped, agencyConnected, tier, oauthStatus };
}

function deriveWordPress(settings: TenantSettings | null): ClientCapabilities['wordpress'] {
  const connected = settings?.wp_connected === true;
  // Credentials entered but the connection never verified = broken, not absent.
  const hasPartialCreds = !connected && Boolean(settings?.wp_user);
  return {
    state: connected ? 'connected' : hasPartialCreds ? 'action_needed' : 'not_set_up',
    connected,
    // WP HONESTY (architect amendment): client-side derivation can only see
    // key PRESENCE — "connected" really means "a key was pasted once". There
    // is no persisted verification timestamp (wp_verified_at does not exist
    // yet), so this is ALWAYS false for now; the pill must never render a
    // bare "Connected" from it. The backend wp_verified_at field flips this
    // to true later without any consumer change.
    verified: false,
  };
}

function deriveGoogleDocs(settings: TenantSettings | null): ClientCapabilities['googleDocs'] {
  const configured = Boolean(
    settings?.gdocs_auto_upload_enabled ||
      settings?.gdocs_folder_id ||
      settings?.bible_doc_id ||
      settings?.calendar_doc_id ||
      settings?.industry_doc_id ||
      settings?.keyword_doc_id ||
      settings?.locations_template_doc_id,
  );
  return {
    state: configured ? 'connected' : 'not_set_up',
    configured,
    // GDocs HONESTY (R7 parity with WordPress): config-presence means a
    // folder/flag was set, NOT that exports have been verified end-to-end.
    // Always false until a backend verification exists; the pill renders the
    // "Not verified" modifier, never a bare "Connected" from config presence.
    verified: false,
  };
}

function deriveBrandBible(bible: BrandBible | null): ClientCapabilities['brandBible'] {
  if (!bible) return { state: 'not_set_up', configured: false };
  const configured = Boolean(bible.business_name?.trim());
  return {
    state: configured ? 'connected' : 'action_needed',
    configured,
  };
}

/**
 * The single source function. Composes the existing payloads into the one
 * capabilities object. To swap in the future backend read, replace this
 * function's body — the key, the shape, and every consumer stay untouched.
 */
async function fetchClientCapabilities(
  tenantId: string,
  workspaceRow: Workspace | undefined,
): Promise<ClientCapabilities> {
  // The Ads OAuth-status + tier joins live HERE (R5) — one query key, one
  // result. Both are Ads-specific and best-effort: a failure leaves the
  // capability intact with agencyConnected=false / tier=null (a tier or
  // status endpoint error never breaks the whole model).
  const [settings, bible, oauthStatus, tier] = await Promise.all([
    apiService.getTenant(tenantId),
    brandBibleService.get(tenantId),
    getConnectionStatus(tenantId).catch(() => null),
    getTenantTier(tenantId)
      .then((r) => r.product_tier ?? null)
      .catch(() => null),
  ]);
  return {
    productPackage: resolveProductPackage(workspaceRow?.product_package),
    googleAds: deriveGoogleAds(workspaceRow, settings, oauthStatus, tier),
    wordpress: deriveWordPress(settings),
    googleDocs: deriveGoogleDocs(settings),
    brandBible: deriveBrandBible(bible),
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseClientCapabilitiesResult {
  /** The verified capabilities object — undefined while unverified. */
  capabilities: ClientCapabilities | undefined;
  isLoading: boolean;
  /** True while any fetch is in flight, including background refetch. */
  isFetching: boolean;
  /** True when the cached data is past staleTime (React Query's isStale). */
  isStale: boolean;
  /**
   * True when the last fetch FAILED. With cached data present, that data is
   * last-known, NOT current — render the stale modifier, never the plain state.
   */
  isError: boolean;
  /** Epoch ms of the last successful fetch — drives "Updated Nm ago". */
  dataUpdatedAt: number;
}

export function useClientCapabilities(tenantId: string | null): UseClientCapabilitiesResult {
  // The workspace list is an existing payload (loaded once per session by
  // ProtectedRoute, refreshed by refreshWorkspaces). The row for THIS tenant
  // feeds product_package + the server-derived has_google_ads flag.
  const workspaceRow = useWorkspaceStore((s) =>
    tenantId
      ? s.workspaces.find(
          (w) => w.website?.trim().toLowerCase() === tenantId.trim().toLowerCase(),
        )
      : undefined,
  );

  const query = useQuery({
    queryKey: tenantId ? clientCapabilitiesKeys.detail(tenantId) : ['client-capabilities', 'none'],
    queryFn: () => fetchClientCapabilities(tenantId as string, workspaceRow),
    enabled: Boolean(tenantId),
    staleTime: 30_000,
    // One retry, then be honest (isError) — never silently present last-known.
    retry: 1,
  });

  return {
    capabilities: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isStale: query.isStale,
    isError: query.isError,
    dataUpdatedAt: query.dataUpdatedAt,
  };
}

// ---------------------------------------------------------------------------
// Write helpers — the ONE invalidation path (+ optimistic-after-write)
// ---------------------------------------------------------------------------

/**
 * Optimistically patch the cached capabilities, then invalidate the ONE key
 * so a background refetch confirms. Same-key invalidation holds the patched
 * data while refetching — the user sees the result of their write instantly
 * (optimistic-after-write) with no flicker.
 */
function writeOptimistic(
  queryClient: QueryClient,
  tenantId: string,
  patch: (prev: ClientCapabilities) => ClientCapabilities,
): void {
  queryClient.setQueryData(
    clientCapabilitiesKeys.detail(tenantId),
    (old: ClientCapabilities | undefined) => (old ? patch(old) : old),
  );
  void queryClient.invalidateQueries({
    queryKey: clientCapabilitiesKeys.detail(tenantId),
  });
}

/**
 * Plain invalidation without an optimistic patch — for writes whose result
 * the derivation must re-read (e.g. a customer ID being CLEARED).
 */
export function invalidateClientCapabilities(
  queryClient: QueryClient,
  tenantId: string,
): void {
  void queryClient.invalidateQueries({
    queryKey: clientCapabilitiesKeys.detail(tenantId),
  });
}

/** Google Ads customer-ID save or detected-ID apply (TenantSettingsPage). */
export function notifyGoogleAdsMappingWritten(
  queryClient: QueryClient,
  tenantId: string,
): void {
  writeOptimistic(queryClient, tenantId, (prev) => ({
    ...prev,
    googleAds: { ...prev.googleAds, customerMapped: true, state: 'connected' },
  }));
}

/** WordPress connect / disconnect success (TenantSettingsPage). */
export function notifyWordPressConnectionWritten(
  queryClient: QueryClient,
  tenantId: string,
  connected: boolean,
): void {
  writeOptimistic(queryClient, tenantId, (prev) => ({
    ...prev,
    wordpress: {
      connected,
      state: connected ? 'connected' : 'not_set_up',
      // An optimistic write can never upgrade the verification claim — the
      // key being saved says nothing about whether it still works.
      verified: false,
    },
  }));
}

/** Brand Bible save (useCreateBrandBible / useUpdateBrandBible). */
export function notifyBrandBibleWritten(
  queryClient: QueryClient,
  tenantId: string,
): void {
  writeOptimistic(queryClient, tenantId, (prev) => ({
    ...prev,
    brandBible: { configured: true, state: 'connected' },
  }));
}
